import { memo, ReactNode, useContext, useMemo, useState } from "react";
import { FormikContext } from "formik";
import { FieldsScopeContext, useFieldsScopeContext } from "./FieldsScopeContext";

const getKeys = (o: Object, parentPath = ""): string[] => {
  const prefix = parentPath ? `${parentPath}.` : "";

  return Object.entries(o || {})
    .map(([ key, value ]) => {
      const propertyPath = `${prefix}${key}`;
      if (Array.isArray(value)) {
        return value.map((v, i) => getKeys(v, `${propertyPath}[${i}]`))
          .reduce((acc, val) => [ ...acc, ...val ], []);
      }
      if (typeof value === "object") {
        return [ ...getKeys(value, propertyPath) ];
      }
      return [ propertyPath ];
    })
    .reduce((acc, val) => [ ...acc, ...val ], []);
};

export const ValidationScope = memo<{ children: ReactNode | ReactNode[] | ((value: boolean) => ReactNode) }>((
  {
    children,
  }) => {
  const { errors, touched, submitCount } = useContext(FormikContext) || {};
  const { registerField: externalRegisterField, unregisterField: externalUnregisterField } = useFieldsScopeContext();
  const [ store, setStore ] = useState<string[]>([]);

  const registerFieldContext = useMemo(() => ({
    registerField: (name: string) => {
      externalRegisterField(name);
      setStore(prevState => [ ...prevState, name ]);
    },
    unregisterField: (name: string) => {
      externalUnregisterField(name);
      setStore(prevState => prevState.filter(x => x !== name));
    },
  }), [ externalRegisterField, externalUnregisterField ]);

  const invalidFields = useMemo(() => submitCount > 0
    ? getKeys(errors)
    : getKeys(errors)
      .filter(fieldName => getKeys(touched)
        .includes(fieldName),
      ), [ errors, touched, submitCount ]);

  const isValid = useMemo(() => {
    return !store.filter(path => invalidFields.includes(path)).length;
  }, [ store, invalidFields ]);

  return (
    <FieldsScopeContext.Provider value={registerFieldContext}>
      {typeof children === "function" ? children(isValid) : children}
    </FieldsScopeContext.Provider>
  );
});
